import { useMemo } from 'react';
import { formatDate } from '@utils/dateHelpers';
import { UseQueryResult, useQueries, useQuery } from '@tanstack/react-query';
import { ApiError } from '@root/globalTypes';
import { queryKeys } from '@utils/consts';
import { queryFetch } from '@services/api';
import { WorkloadCalendarRespSuccess } from './types';
import { useQueryParams } from '@hooks/queryParams';
import { useIsPhone } from '@hooks/useIsPhone';
import { getFirstDayOfBusinessWeek } from '@contexts/workload/constants';
import { computeWeeklyDrafts, filterWorkloadWeeklyRecords } from './hiddenRecords';
import { useWorkloadFilterParams } from '@hooks/workload/filterParams';

const isWeekType = (type?: string) => !type || type === 'week';

const useWorkloadCalendarQuery = () => {
  const { params, parseParams } = useQueryParams();
  const isPhone = useIsPhone();

  const {
    type,
    search,
    unassigned,
    sortingValue,
    sortingDirection,
    secondarySortingValue,
    secondarySortingDirection,
  } = params;

  return (date: Date) => {
    const queryParams = parseParams(
      {
        date: type !== 'day' ? formatDate(getFirstDayOfBusinessWeek(date)) : formatDate(date),
        search,
        unassigned,
        sortingValue,
        sortingDirection,
        secondarySortingValue,
        secondarySortingDirection,
        calendarType: type,
        displayEndpoint:
          isPhone && (type === 'day' || isWeekType(type)) ? 'mobile_day_calendar' : undefined,
      },
      { arrayFormat: 'bracket' },
    );

    return {
      queryKey:
        type !== 'day'
          ? [...queryKeys.workloadWeeklyCalendar, queryParams]
          : [...queryKeys.workloadDaily, queryParams],
      queryFn: async (): Promise<WorkloadCalendarRespSuccess> =>
        await queryFetch({
          url: `workloads/calendar${queryParams}`,
        }),
      enabled: !type || ['week', 'day'].includes(type),
    };
  };
};

const combineCalendarRanges = (results: UseQueryResult<WorkloadCalendarRespSuccess>[]) =>
  results.map((result) => ({
    records: result.data?.records || [],
    withoutTeam: result.data?.withoutTeam || [],
    loading: result.isPending && result.isFetching,
  }));

export const useGetWorkloadCalendarRanges = (starts: Date[]) => {
  const getQuery = useWorkloadCalendarQuery();

  return useQueries({
    queries: starts.map((start) => getQuery(start)),
    combine: combineCalendarRanges,
  });
};

export const useGetWorkloadCalendar = (date: Date, weekDates?: Date[]) => {
  const { params } = useQueryParams();
  const { type } = params;
  const getQuery = useWorkloadCalendarQuery();

  const { data, error, isPending, isPlaceholderData, isFetching } = useQuery<
    WorkloadCalendarRespSuccess,
    ApiError
  >({
    ...getQuery(date),
    placeholderData: (previousData) => previousData as WorkloadCalendarRespSuccess,
  });

  const { filterParams, filterKey } = useWorkloadFilterParams();
  const windowDates = type === 'week' ? weekDates : undefined;
  const windowKey = windowDates?.map((day) => formatDate(day)).join('_') || '';

  const filtered = useMemo(
    () =>
      filterWorkloadWeeklyRecords(
        data?.records || [],
        data?.withoutTeam || [],
        filterParams,
        windowDates,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.records, data?.withoutTeam, filterKey, windowKey],
  );

  const filteredAllDates = useMemo(
    () =>
      windowDates
        ? filterWorkloadWeeklyRecords(data?.records || [], data?.withoutTeam || [], filterParams)
        : filtered,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.records, data?.withoutTeam, filterKey, windowKey, filtered],
  );

  const drafts = useMemo(
    () => computeWeeklyDrafts(filtered.visibleForemen),
    [filtered.visibleForemen],
  );

  return {
    data,
    visibleRecords: filtered.visibleForemen,
    visibleWithoutTeam: filtered.visibleWithoutTeam,
    allDatesVisibleRecords: filteredAllDates.visibleForemen,
    allDatesVisibleWithoutTeam: filteredAllDates.visibleWithoutTeam,
    hiddenCount: filtered.hiddenCount,
    hiddenForemenCount: filtered.hiddenForemenCount,
    drafts,
    error,
    isLoading: isFetching && (isPending || isPlaceholderData),
  };
};
